'use client';

import { Box, Stack, SxProps, Tab, TabProps, Tabs, TabsProps, alpha, styled } from '@mui/material';
import React, { type ReactNode, useState } from 'react';

export interface TabOption {
  key: string;
  label: string;
  content?: React.ReactNode;
  disabled?: boolean;
}

interface GenericTabsProps<T> {
  options: TabOption[];
  renderContentTab?: (item: T, index: number) => React.ReactNode;
  defaultValue?: string;
  tabsProps?: TabsProps;
  tabProps?: TabProps;
  tabPanelSx?: SxProps;
  onChange?: (value: string) => void;
  extraTab?: ReactNode;
}

const AntTabs = styled(Tabs)({
  '& .MuiTabs-flexContainer': {
    gap: '16px',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: 'primary.main',
  },
  '& .MuiTabScrollButton-root': {
    color: '#FFFFFF',
  },
});

const BasicTabs = <T,>({
  options,
  defaultValue,
  tabsProps,
  tabProps,
  tabPanelSx,
  renderContentTab,
  onChange,
  extraTab,
}: GenericTabsProps<T>) => {
  const [currentTab, setCurrentTab] = useState<string>(defaultValue || options[0]?.key || '');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
    onChange?.(newValue);
  };

  return (
    <Box>
      {/* Tabs */}
      <Stack
        width={'100%'}
        justifyContent={extraTab ? 'space-between' : 'start'}
        flexDirection={'row'}
        alignItems={'center'}>
        <AntTabs
          value={currentTab}
          scrollButtons='auto'
          variant='scrollable'
          allowScrollButtonsMobile
          onChange={handleTabChange}
          {...tabsProps}>
          {options.map(option => (
            <Tab
              key={option.key}
              label={option.label}
              value={option.key}
              disabled={option.disabled ?? false}
              disableRipple
              {...tabProps}
              sx={{
                height: '52px',
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '20px',
                fontFamily: 'Gotham',
                textAlign: 'right',
                textTransform: 'none',
                color: '#767272',
                '&.Mui-selected': {
                  color: '#FFFFFF',
                },
                '&.Mui-disabled': {
                  color: 'text.secondary',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: '2px',
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.06),
                },
              }}
            />
          ))}
        </AntTabs>
        {extraTab}
      </Stack>

      {/* Tab Panels */}
      {renderContentTab &&
        options.map((option: any, index: number) => (
          <Box
            key={option.key}
            role='tabpanel'
            hidden={currentTab !== option.key}
            sx={{
              p: 2,
              ...tabPanelSx,
            }}>
            {currentTab === option.key && renderContentTab(option, index)}
          </Box>
        ))}
    </Box>
  );
};

export default BasicTabs;

BasicTabs.displayName = 'BasicTabs';
